import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import Navbar from "@/components/Navbar";
import PageBackground from "@/components/PageBackground";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Check, Sparkles, Loader2 } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { UpgradeDialog } from "@/components/UpgradeDialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { useRazorpayPayment } from "@/hooks/useRazorpayPayment";
import { usePaymentProvider } from "@/hooks/usePaymentProvider";
import { PayPalButton } from "@/components/PayPalButton";

// Pricing differs based on payment provider/country
const getPlans = (isIndianUser: boolean) => [{
  name: "Basic",
  value: "basic" as const,
  limit: isIndianUser ? 20 : 50,
  limitType: "monthly",
  price: isIndianUser ? "₹149/mo" : "$3.99/mo",
  features: [`${isIndianUser ? "20" : "50"} stories/month`, "Audio narration"],
  missingFeatures: ["Downloads", "Story and character personalization"]
}, {
  name: "Premium",
  value: "premium" as const,
  limit: isIndianUser ? 50 : 100,
  limitType: "monthly",
  price: isIndianUser ? "₹279/mo" : "$5.99/mo",
  features: [`${isIndianUser ? "50" : "100"} stories/month`, "Audio narration", "Personalization", "Unlimited downloads"],
  popular: true
}];

const Account = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [childName, setChildName] = useState("");
  const [childAge, setChildAge] = useState<number | "">("");
  const [childGender, setChildGender] = useState("");
  

  // Subscription state
  const [currentPlan, setCurrentPlan] = useState<string>("free");
  const [storiesUsed, setStoriesUsed] = useState(0);
  const [storyLimit, setStoryLimit] = useState(3);
  const [isExpired, setIsExpired] = useState(false);
  const [expiresAt, setExpiresAt] = useState<string | null>(null);
  const [cancelledAt, setCancelledAt] = useState<string | null>(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(true);
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [freeStoriesRemaining, setFreeStoriesRemaining] = useState(3);
  const [carryoverStories, setCarryoverStories] = useState(0);
  const [carryoverValid, setCarryoverValid] = useState(false);
  const [pendingPlan, setPendingPlan] = useState<string | null>(null);
  const [pendingPlanEffectiveAt, setPendingPlanEffectiveAt] = useState<string | null>(null);
  const [providerSubscriptionId, setProviderSubscriptionId] = useState<string | null>(null);
  const [paymentProvider, setPaymentProvider] = useState<string | null>(null);
  const [cancellingSubscription, setCancellingSubscription] = useState(false);
  
  const { initiatePayment, loadingPlan } = useRazorpayPayment();
  const { provider, isLoading: providerLoading } = usePaymentProvider();
  
  // Get plans based on user's payment provider (Indian users use Razorpay)
  const isIndianUser = provider === "razorpay";
  const plans = getPlans(isIndianUser);

  useEffect(() => {
    checkUser();
    fetchProfile();
    fetchSubscription();
  }, []);

  const checkUser = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      navigate("/auth");
    }
  };

  const fetchProfile = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        setEmail(user.email || "");
        const { data: profile } = await supabase.from('profiles').select('child_name, child_age, child_gender').eq('id', user.id).single();
        if (profile) {
          setChildName(profile.child_name || "");
          setChildAge(profile.child_age || "");
          setChildGender(profile.child_gender || "");
        }
      }
    } catch (error) {
      console.error("Error fetching profile:", error);
    }
  };

  const fetchSubscription = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        // Get effective plan using RPC (handles expiry automatically)
        const { data: planData } = await supabase.rpc('get_effective_plan', {
          user_uuid: user.id
        });
        if (planData && planData.length > 0) {
          const { plan, is_expired, expires_at, cancelled_at } = planData[0];
          setCurrentPlan(plan || 'free');
          setIsExpired(is_expired || false);
          setExpiresAt(expires_at || null);
          setCancelledAt(cancelled_at || null);

          // Set base limit based on effective plan
          const baseLimit = plan === 'premium' ? 100 : plan === 'basic' ? 50 : 3;
          setStoryLimit(baseLimit);
        }

        // Fetch pending plan and subscription info directly from subscriptions table
        const { data: subData } = await supabase
          .from('subscriptions')
          .select('pending_plan, pending_plan_effective_at, provider_subscription_id, payment_provider')
          .eq('user_id', user.id)
          .maybeSingle();
        
        if (subData) {
          setPendingPlan(subData.pending_plan);
          setPendingPlanEffectiveAt(subData.pending_plan_effective_at);
          setProviderSubscriptionId(subData.provider_subscription_id);
          setPaymentProvider(subData.payment_provider);
        }

        // Get effective story limit including carryover
        const { data: limitData } = await supabase.rpc('get_effective_story_limit', {
          user_uuid: user.id
        });
        if (limitData && limitData.length > 0) {
          const { base_limit, carryover_amount, carryover_valid, total_limit } = limitData[0];
          setStoryLimit(total_limit);
          setCarryoverStories(carryover_amount || 0);
          setCarryoverValid(carryover_valid || false);
        }

        // Get story count using RPC (already handles expiry)
        const { data: count } = await supabase.rpc('get_user_story_count', {
          user_uuid: user.id
        });
        setStoriesUsed(count || 0);
        
        // Get free stories remaining
        const { data: freeRemaining } = await supabase.rpc('get_free_stories_remaining', { user_uuid: user.id });
        setFreeStoriesRemaining(freeRemaining ?? 3);
      }
    } catch (error) {
      console.error("Error fetching subscription:", error);
    } finally {
      setSubscriptionLoading(false);
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const getDaysRemaining = (dateStr: string) => {
    const expiry = new Date(dateStr);
    const now = new Date();
    const diffTime = expiry.getTime() - now.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  const handleUpdateProfile = async () => {
    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { error } = await supabase.from('profiles').update({
          child_name: childName || null,
          child_age: childAge || null,
          child_gender: childGender || null
        }).eq('id', user.id);
        if (error) throw error;
        toast({
          title: "Success",
          description: "Profile updated successfully"
        });
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateEmail = async () => {
    setLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({ email });
      if (error) throw error;
      toast({
        title: "Success",
        description: "Email update initiated. Please check your inbox to confirm."
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async () => {
    setLoading(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/auth`
      });
      if (error) throw error;
      toast({
        title: "Success",
        description: "Password reset email sent. Please check your inbox."
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancelSubscription = () => {
    setShowCancelDialog(true);
  };

  const handleSubscribe = (planValue: "basic" | "premium") => {
    initiatePayment({
      plan: planValue,
      onSuccess: () => {
        fetchSubscription();
      },
    });
  };

  const handlePayPalSuccess = useCallback(() => {
    fetchSubscription();
  }, []);

  const confirmCancelSubscription = async () => {
    if (!providerSubscriptionId) {
      toast({
        title: "No Active Subscription",
        description: "No active subscription found to cancel.",
        variant: "destructive"
      });
      setShowCancelDialog(false);
      return;
    }

    setCancellingSubscription(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error("Not authenticated");
      }

      // Use appropriate edge function based on payment provider
      if (paymentProvider === 'paypal') {
        const { data, error } = await supabase.functions.invoke('cancel-paypal-subscription', {
          body: { 
            subscription_id: providerSubscriptionId,
            reason: "User requested cancellation"
          }
        });

        if (error) throw error;
      } else if (paymentProvider === 'razorpay') {
        const { data, error } = await supabase.functions.invoke('cancel-razorpay-subscription');

        if (error) throw error;
      } else {
        throw new Error("Unknown payment provider");
      }

      toast({
        title: "Subscription Cancelled",
        description: "Your subscription has been cancelled. You'll retain access until the end of your billing period."
      });
      
      // Refresh subscription data
      fetchSubscription();
    } catch (error: any) {
      console.error("Error cancelling subscription:", error);
      toast({
        title: "Cancellation Failed",
        description: error.message || "Failed to cancel subscription. Please try again or contact support.",
        variant: "destructive"
      });
    } finally {
      setCancellingSubscription(false);
    }
    setShowCancelDialog(false);
  };

  return (
    <PageBackground>
      <Navbar />
      
      <main className="container mx-auto px-4 py-8 md:py-12 pb-24 md:pb-12">
        <div className="max-w-3xl mx-auto animate-fade-in">
          <h2 className="text-2xl md:text-3xl font-semibold text-foreground mb-4 md:mb-6 font-sans">
            Account Settings
          </h2>

          <div className="space-y-4 md:space-y-5">
            {/* Plan & Usage Info */}
            <Card className="p-4 md:p-5">
              <h3 className="text-lg md:text-xl font-semibold text-foreground mb-3 md:mb-4">Plan & Usage</h3>
              
              {subscriptionLoading || providerLoading ? (
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <Skeleton className="h-4 w-24" />
                    <Skeleton className="h-6 w-20 rounded-full" />
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <Skeleton className="h-4 w-28" />
                      <Skeleton className="h-3 w-20" />
                    </div>
                    <Skeleton className="h-2 w-full" />
                  </div>
                  <div className="pt-4 border-t">
                    <Skeleton className="h-4 w-32 mb-3" />
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <Skeleton className="h-40 w-full rounded-lg" />
                      <Skeleton className="h-40 w-full rounded-lg" />
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Current Plan Badge */}
                  <div className="flex flex-wrap items-center gap-2 md:gap-3">
                    <span className="text-sm text-muted-foreground">Current Plan:</span>
                    <Badge 
                      variant={
                        currentPlan === 'free' && storiesUsed >= storyLimit 
                          ? "secondary" 
                          : isExpired 
                            ? "destructive" 
                            : "default"
                      }
                    >
                      {currentPlan === 'free' 
                        ? (storiesUsed >= storyLimit ? "No active plan" : "Free Trial")
                        : currentPlan.charAt(0).toUpperCase() + currentPlan.slice(1)}
                      {isExpired && currentPlan !== 'free' && " (Expired)"}
                    </Badge>
                  </div>

                  {/* Pending Downgrade Notice */}
                  {pendingPlan && pendingPlanEffectiveAt && (
                    <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
                      <p className="text-sm text-blue-800 dark:text-blue-200">
                        ⏳ Your plan will change to <strong>{pendingPlan.charAt(0).toUpperCase() + pendingPlan.slice(1)}</strong> on <strong>{formatDate(pendingPlanEffectiveAt)}</strong>. You'll keep full {currentPlan.charAt(0).toUpperCase() + currentPlan.slice(1)} access until then.
                      </p>
                    </div>
                  )}

                  {/* Cancellation Notice */}
                  {cancelledAt && !isExpired && expiresAt && currentPlan !== 'free' && !pendingPlan && (
                    <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
                      <p className="text-sm text-amber-800 dark:text-amber-200">
                        ⚠️ Your subscription has been cancelled. You'll retain access to your {currentPlan.charAt(0).toUpperCase() + currentPlan.slice(1)} plan until <strong>{formatDate(expiresAt)}</strong>.
                      </p>
                    </div>
                  )}

                  {/* Billing/Expiry Info */}
                  {expiresAt && !cancelledAt && (
                    <div className="text-sm">
                      {isExpired ? (
                        <p className="text-destructive">
                          Expired on: {formatDate(expiresAt)}
                        </p>
                      ) : currentPlan !== 'free' && (
                        <p className="text-muted-foreground">
                          Next billing: {formatDate(expiresAt)}
                        </p>
                      )}
                    </div>
                  )}

                  {/* Show expired message for cancelled subscriptions that have expired */}
                  {isExpired && cancelledAt && expiresAt && (
                    <div className="text-sm">
                      <p className="text-destructive">
                        Expired on: {formatDate(expiresAt)}
                      </p>
                    </div>
                  )}
                  
                  {/* Free Stories Display */}
                  {freeStoriesRemaining > 0 && (
                    <div className="bg-primary/10 border border-primary/20 rounded-lg p-3">
                      <div className="flex items-center gap-2">
                        <Sparkles className="w-4 h-4 text-primary" />
                        <span className="text-sm font-medium text-primary">
                          {freeStoriesRemaining} free {freeStoriesRemaining === 1 ? 'story' : 'stories'} remaining
                        </span>
                      </div>
                    </div>
                  )}
                  
                  {/* Usage Display */}
                  {currentPlan !== 'free' && (
                    <div className="space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium">
                          {storiesUsed} / {storyLimit} stories
                          {carryoverValid && carryoverStories > 0 && (
                            <span className="text-xs text-muted-foreground ml-1">
                              (includes {carryoverStories} carryover)
                            </span>
                          )}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          (this month)
                        </span>
                      </div>
                      <Progress value={storiesUsed / storyLimit * 100} className="h-2" />
                      {carryoverValid && carryoverStories > 0 && (
                        <p className="text-xs text-primary">
                          ✨ {carryoverStories} bonus stories from your previous plan carry over this month!
                        </p>
                      )}
                    </div>
                  )}

                  {/* Plan Actions */}
                  {currentPlan === 'free' ? (
                    <div className="pt-4 border-t">
                      <h4 className="text-sm font-medium mb-3">Available Plans</h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {plans.map(plan => (
                          <Card key={plan.value} className={`p-3 relative flex flex-col ${plan.popular ? "border-primary/50" : ""}`}>
                            {plan.popular && (
                              <Badge className="absolute -top-2 left-1/2 -translate-x-1/2 bg-primary text-xs">
                                Popular
                              </Badge>
                            )}
                            <div className="text-center mt-1">
                              <h5 className="font-semibold text-sm">{plan.name}</h5>
                              <p className="text-lg font-bold text-primary">{plan.price}</p>
                            </div>
                            <ul className="mt-2 space-y-1 flex-grow mb-3">
                              {plan.features?.map(feature => (
                                <li key={feature} className="flex items-center gap-1 text-xs">
                                  <Check className="w-3 h-3 text-primary flex-shrink-0" />
                                  <span>{feature}</span>
                                </li>
                              ))}
                            </ul>
                            
                            {/* Payment button based on provider */}
                            {provider === "paypal" ? (
                              <PayPalButton key={`paypal-account-${plan.value}`} plan={plan.value} onSuccess={handlePayPalSuccess} />
                            ) : (
                              <Button 
                                size="sm" 
                                variant={plan.popular ? "default" : "outline"} 
                                className="mt-auto w-full text-xs h-10"
                                onClick={() => handleSubscribe(plan.value)}
                                disabled={loadingPlan !== null}
                              >
                                {loadingPlan === plan.value ? (
                                  <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                ) : (
                                  <Sparkles className="w-3 h-3 mr-1" />
                                )}
                                Subscribe
                              </Button>
                            )}
                          </Card>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="pt-4 border-t flex flex-col sm:flex-row gap-2 sm:gap-3">
                      <Button onClick={() => setShowUpgradeDialog(true)} variant="outline" className="w-full sm:w-auto">
                        <Sparkles className="w-4 h-4 mr-2" />
                        Change plan
                      </Button>
                      {!isExpired && !cancelledAt && (
                        <Button onClick={handleCancelSubscription} variant="ghost" className="text-destructive hover:text-destructive w-full sm:w-auto">
                          Cancel subscription
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              )}
            </Card>

            {/* Email & Password */}
            <Card className="p-4 md:p-5">
              <h3 className="text-lg md:text-xl font-semibold text-foreground mb-3">Email & Password</h3>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="email">Email Address</Label>
                  <Input id="email" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="your@email.com" className="mt-2" />
                  <Button onClick={handleUpdateEmail} disabled={loading} className="mt-2 w-full sm:w-auto" variant="outline">
                    Update Email
                  </Button>
                </div>
                <div className="pt-4 border-t">
                  <Button onClick={handleResetPassword} disabled={loading} variant="outline" className="w-full sm:w-auto">
                    Send Password Reset Email
                  </Button>
                </div>
              </div>
            </Card>

            {/* Child Profile */}
            <Card className="p-4 md:p-5">
              <h3 className="text-lg md:text-xl font-semibold text-foreground mb-3">Child Profile</h3>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="childName">Child's Name</Label>
                  <Input id="childName" value={childName} onChange={e => setChildName(e.target.value)} placeholder="Enter child's name" className="mt-2" />
                </div>
                <div>
                  <Label htmlFor="childAge">Child's Age</Label>
                  <Input id="childAge" type="number" value={childAge} onChange={e => setChildAge(e.target.value ? parseInt(e.target.value) : "")} placeholder="Enter age" className="mt-2" min="1" max="18" />
                </div>
                <div>
                  <Label htmlFor="childGender">Child's Gender</Label>
                  <Select value={childGender} onValueChange={setChildGender}>
                    <SelectTrigger className="mt-2">
                      <SelectValue placeholder="Select gender" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="boy">Boy</SelectItem>
                      <SelectItem value="girl">Girl</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button onClick={handleUpdateProfile} disabled={loading} className="w-full h-11">
                  Save Profile
                </Button>
              </div>
            </Card>
          </div>
        </div>
      </main>

      <UpgradeDialog 
        open={showUpgradeDialog} 
        onOpenChange={setShowUpgradeDialog} 
        currentPlan={currentPlan} 
        storiesUsed={storiesUsed} 
        limit={storyLimit} 
        isExpired={isExpired} 
        onPaymentSuccess={fetchSubscription} 
      />
      
      <AlertDialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel Subscription?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to cancel your {currentPlan} subscription? You'll lose access to premium features at the end of your billing period.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep Subscription</AlertDialogCancel>
            <AlertDialogAction onClick={confirmCancelSubscription} disabled={cancellingSubscription} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              {cancellingSubscription ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Cancelling...
                </>
              ) : (
                "Yes, Cancel"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PageBackground>
  );
};

export default Account;
